import { useState, useRef } from "react";
import { Objective } from "@/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { ObjectiveForm } from "./ObjectiveForm";

interface ObjectiveDialogsProps {
  isFormOpen: boolean;
  onFormOpenChange: (open: boolean) => void;
  onObjectiveSaved?: (objective: Objective) => void;
  editingObjective: Objective | null;
  isDeleteOpen: boolean;
  onDeleteOpenChange: (open: boolean) => void;
  objectiveToDelete: Objective | null;
  onConfirmDelete: () => void;
  isDeleting: boolean;
}

export function ObjectiveDialogs({
  isFormOpen,
  onFormOpenChange,
  onObjectiveSaved,
  editingObjective,
  isDeleteOpen,
  onDeleteOpenChange,
  objectiveToDelete,
  onConfirmDelete,
  isDeleting,
}: ObjectiveDialogsProps) {
  const submitRef = useRef<(() => void) | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <>
      <Dialog open={isFormOpen} onOpenChange={onFormOpenChange}>
        <DialogContent className="sm:max-w-[50vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingObjective ? "Edit Objective" : "Create Objective"}</DialogTitle>
          </DialogHeader>
          <ObjectiveForm
            key={editingObjective?.pid || "new"}
            initialData={editingObjective || undefined}
            onSuccess={(objective) => {
              onFormOpenChange(false);
              onObjectiveSaved?.(objective);
            }}
            hideButtons
            onSubmitRef={(fn) => { submitRef.current = fn; }}
            onSubmittingChange={setIsSubmitting}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => onFormOpenChange(false)}>Cancel</Button>
            <Button onClick={() => submitRef.current?.()} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingObjective ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteOpen} onOpenChange={onDeleteOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Objective</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{objectiveToDelete?.name}"? This action cannot be undone.
              {objectiveToDelete?.keyResults && objectiveToDelete.keyResults.length > 0 && (
                <span className="block mt-2 font-medium text-destructive">
                  Warning: This objective has {objectiveToDelete.keyResults.length} key result(s).
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmDelete} disabled={isDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
